import { stripPointing } from '@app/shared'

/**
 * How many dedications a board shows before it folds.
 *
 * Up to this many rows a donor takes the whole board in at a glance, and a
 * field above it would be a control with nothing to narrow. Past it — a
 * building campaign runs to a room, a window and a seat each — the board shows
 * this many, the next one peeking, and one control opening the rest; a search
 * field finds the one somebody came for across all of them.
 *
 * It folds rather than scrolling inside itself at every width: a scroll region
 * inside a scrolling page catches a finger on a phone, and the page can no
 * longer be moved past it.
 */
export const BOARD_SEARCH_AT = 8

export const boardIsLong = (rows: number): boolean => rows > BOARD_SEARCH_AT

/**
 * Whether a dedication's label answers what was typed.
 *
 * A label is written in whichever language the shul names it, so both sides
 * are compared without niqqud or taamim — a pointed label must answer a donor
 * typing plain letters — and case-folded, which moves Latin and leaves Hebrew
 * as it is. An empty query answers everything.
 */
export function matchesBoardSearch(label: string, query: string): boolean {
  const q = fold(query)
  return q === '' || fold(label).includes(q)
}

const fold = (text: string): string =>
  stripPointing(text).toLocaleLowerCase().replace(/\s+/g, ' ').trim()

export type BoardView<T> = {
  /** The rows drawn in full, in board order. */
  shown: T[]
  /** The row drawn cut and inert beneath them, saying there is more. */
  peek: T | null
  /** Whether the board is folded, and so offers to show all of it. */
  folded: boolean
}

/**
 * Which rows a board draws.
 *
 * A search reaches every row whether or not the board is folded, and shows all
 * it finds: what somebody typed for is never behind a second control. Folded,
 * the board keeps any row in `keep` — the dedication somebody chose is never
 * folded away from them — and peeks the first row it leaves out.
 */
export function boardView<T extends { id: string; label: string }>(
  rows: T[],
  { query, open, keep }: { query: string; open: boolean; keep: string[] },
): BoardView<T> {
  if (!boardIsLong(rows.length))
    return { shown: rows, peek: null, folded: false }
  if (fold(query) !== '')
    return {
      shown: rows.filter((r) => matchesBoardSearch(r.label, query)),
      peek: null,
      folded: false,
    }
  if (open) return { shown: rows, peek: null, folded: false }
  const shown = rows.filter(
    (r, i) => i < BOARD_SEARCH_AT || keep.includes(r.id),
  )
  const peek = rows.find((r) => !shown.includes(r)) ?? null
  return { shown, peek, folded: peek !== null }
}
